import { useEffect, useState } from 'react';
import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type {
    loader,
    NetworkProfessional,
} from '@modules/professional/page/loaders/overview-page-loader.server';
import {
    detailCta,
    TileCarousel,
    type DetailTile,
} from '~/ui-rocco/pages/detail-shared';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { CategoryPills } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import type { CategoryPill } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import { DiscoverHeader } from '~/ui-rocco/pages/home-page/components/DiscoverHeader';
import { formatSectionNumber } from '../utils/section-numbering';

const NETWORK_DESCRIPTION =
    "We've curated New Zealand's leading professionals so you can find the best fit for your project. Browse their work, discover their services and enquire directly from ArchiPro.";

interface ProfessionalNetworkSectionProps {
    sectionNumber?: number;
}

interface NetworkGroup {
    category: string;
    items: { pro: NetworkProfessional; index: number }[];
}

/**
 * §7 — (03) Who we work with. A big two-line heading, then a two-column body:
 * Popular-categories pills (the aggregated network categories) above the active
 * partner's portrait on the left; the description + View-our-Network CTA above a
 * 2-column, category-grouped name list on the right. Hovering a name swaps the
 * portrait; the first partner is active by default. The names align to the
 * category header — the active arrow hangs in the left margin.
 */
export const ProfessionalNetworkSection = ({
    sectionNumber,
}: ProfessionalNetworkSectionProps = {}) => {
    const { networkProfessionals, professionalOverview } =
        useLoaderData<typeof loader>();
    const { desktop } = useAppDisplayModeContext();
    const [activeIndex, setActiveIndex] = useState(0);

    // Warm the browser cache for every partner banner so the desktop hover-swaps
    // are instant. Mobile uses a lazy-loaded carousel, so skip the preload there.
    useEffect(() => {
        if (!networkProfessionals || !desktop) return;
        const preloaded = networkProfessionals.map((pro) => {
            const img = new Image();
            img.src = pro.image;
            return img;
        });
        return () => {
            preloaded.forEach((img) => {
                img.src = '';
            });
        };
    }, [networkProfessionals, desktop]);

    if (!networkProfessionals || networkProfessionals.length === 0) return null;

    // Mobile shows the partners as a standard tile carousel (matching the other
    // detail-page related rails) rather than the desktop hover-swap module.
    const networkTiles: DetailTile[] = networkProfessionals.map(
        (pro, index) => ({
            id: index,
            link: pro.link,
            title: pro.title,
            subtitle: pro.category,
            images: [{ src: pro.image, alt: pro.title }],
            logo: pro.logo,
            logoBackground: pro.logoBackground,
        }),
    );

    const groups = networkProfessionals.reduce<NetworkGroup[]>(
        (acc, pro, index) => {
            let group = acc.find((entry) => entry.category === pro.category);
            if (!group) {
                group = { category: pro.category, items: [] };
                acc.push(group);
            }
            group.items.push({ pro, index });
            return acc;
        },
        [],
    );

    const popularCategories: CategoryPill[] = groups
        .slice(0, 6)
        .map((group) => ({
            label: group.category,
            to: `${professionalOverview.Link}/partners`,
        }));

    const active = networkProfessionals[activeIndex] ?? networkProfessionals[0];
    if (!active) return null;

    return (
        <section className="bg-white md:px-[3rem]">
            <div className="mx-auto w-full max-w-[114rem]">
                {/* Mobile: the shared Discover header (eyebrow → heading → network
                categories + copy + CTA) over a partner carousel, matching the
                sibling Articles/Team section headers. */}
                <div className="md:hidden">
                    <DiscoverHeader
                        theme="light"
                        fullWidthRule
                        unit="rem"
                        flushTop
                        stackOnMobile
                        eyebrow={`(${formatSectionNumber(sectionNumber ?? 3)} — Who we work with)`}
                        heading="Who we work with"
                        categories={popularCategories}
                        description={NETWORK_DESCRIPTION}
                        ctaLabel="View our Network"
                        ctaTo={`${professionalOverview.Link}/partners`}
                        ctaClassName={detailCta(
                            'filled-light',
                            'w-fit max-md:h-[2.75rem] max-md:px-6 max-md:text-sm',
                        )}
                        headingClassName="font-sans max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em]"
                    />
                    <TileCarousel
                        hideHeader
                        hidePagination
                        eyebrow="Who we work with"
                        tiles={networkTiles}
                        theme="light"
                        viewportClassName="px-6"
                        contentClassName="max-md:gap-2"
                        className="max-md:pt-8"
                    />
                </div>

                {/* Desktop: the editorial hover-swap module. */}
                <div className="max-md:hidden">
                    <div className="h-px w-full bg-black/20" />
                    <SectionLabel className="mt-[1.062rem] block text-[#181818] md:text-[2rem]">
                        ({formatSectionNumber(sectionNumber ?? 3)} — Who we work
                        with)
                    </SectionLabel>

                    <h2 className="m-0 font-sans font-normal text-[#141414] md:mt-[5.75rem] md:max-w-[66rem] md:text-[12.5rem] md:leading-[0.92] md:tracking-[-0.12rem]">
                        Who we work with
                    </h2>

                    <div className="md:mt-[16.25rem] md:flex md:gap-[14.4rem]">
                        {/* Left column: popular categories + the active portrait. */}
                        <div className="shrink-0 md:w-[44.4rem]">
                            <CategoryPills
                                label="Popular categories:"
                                items={popularCategories}
                                className="max-md:mt-8 md:gap-[1.5625rem]"
                            />
                            <div className="bg-neutral-200 relative aspect-[696/877] w-full overflow-hidden rounded-tile max-md:mt-8 md:left-[2.3rem] md:mt-[16.6875rem]">
                                <img
                                    key={active.image}
                                    src={active.image}
                                    alt={active.title}
                                    className="absolute inset-0 size-full object-cover"
                                />
                            </div>
                        </div>

                        {/* Right column: description + CTA, then the grouped name lists. */}
                        <div className="min-w-0 flex-1 max-md:mt-10">
                            <p className="m-0 max-w-[52.8rem] font-sans leading-[1.4] font-normal text-[#1f1f21] max-md:max-w-none max-md:text-base md:-mt-[0.75rem] md:text-[2.4375rem]">
                                {NETWORK_DESCRIPTION}
                            </p>
                            <RoccoLink
                                to={`${professionalOverview.Link}/partners`}
                                className={detailCta(
                                    'filled-light',
                                    'w-fit max-md:mt-6 md:mt-[2.4rem]',
                                )}
                            >
                                View our Network
                            </RoccoLink>

                            <div className="columns-2 gap-[4.8rem] max-md:mt-10 md:mt-[11.5625rem]">
                                {groups.map((group) => (
                                    <div
                                        key={group.category}
                                        className="break-inside-avoid max-md:mb-8 md:mb-[2.1rem]"
                                    >
                                        <p className="m-0 font-sans tracking-[-0.03em] text-[#323232] uppercase max-md:text-xs md:text-[1.0625rem]">
                                            {group.category}
                                        </p>
                                        <ul className="m-0 mt-[0.48rem] list-none p-0 max-md:mt-2">
                                            {group.items.map(
                                                ({ pro, index }) => {
                                                    const isActive =
                                                        index === activeIndex;
                                                    return (
                                                        <li key={index}>
                                                            <RoccoLink
                                                                to={pro.link}
                                                                onMouseEnter={() =>
                                                                    setActiveIndex(
                                                                        index,
                                                                    )
                                                                }
                                                                onFocus={() =>
                                                                    setActiveIndex(
                                                                        index,
                                                                    )
                                                                }
                                                                className={cn(
                                                                    'relative block font-sans leading-[1.03] tracking-[-0.03em] transition-colors max-md:py-1 max-md:text-base md:py-[0.34rem] md:text-[1.657rem]',
                                                                    isActive
                                                                        ? 'text-[#181818]'
                                                                        : 'text-[#bbb] hover:text-[#181818]',
                                                                )}
                                                            >
                                                                <svg
                                                                    viewBox="0 0 8 10"
                                                                    aria-hidden="true"
                                                                    className={cn(
                                                                        'absolute top-1/2 right-full size-[0.45em] -translate-y-1/2 fill-current transition-opacity max-md:mr-1 md:mr-[0.48rem]',
                                                                        isActive
                                                                            ? 'opacity-100'
                                                                            : 'opacity-0',
                                                                    )}
                                                                >
                                                                    <path d="M0 0l8 5-8 5z" />
                                                                </svg>
                                                                {pro.title}
                                                            </RoccoLink>
                                                        </li>
                                                    );
                                                },
                                            )}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
